import { useState } from 'react';

export default function ExecEdit({ audit, onUpdate }) {
  const ai = audit.aiResearch || {};
  const [refinePrompt, setRefinePrompt] = useState('');
  const [refining, setRefining] = useState(false);
  const [refineTarget, setRefineTarget] = useState('diagnostic');

  const execDiagnostic = audit.execDiagnosticOverride || ai.executiveDiagnostic || '';
  const businessOnePage = audit.businessOnePageOverride || ai.businessInOnePage || '';
  const whatSuccess = audit.whatSuccessOverride || ai.whatSuccessLooksLike || '';

  const updateField = (field, value) => {
    onUpdate({ ...audit, [field]: value });
  };

  const refineWithAI = async () => {
    if (!refinePrompt.trim()) return;
    setRefining(true);

    try {
      const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
      const currentText = refineTarget === 'diagnostic' ? execDiagnostic
        : refineTarget === 'onePage' ? businessOnePage : whatSuccess;

      const prompt = `You are writing for a premium hospitality consulting firm (Next Experience Group). Rewrite the following section based on the instruction.

CURRENT TEXT:
"${currentText}"

INSTRUCTION: ${refinePrompt}

CONTEXT:
Concept: ${audit.intake?.conceptName || 'Unknown'}
City: ${audit.intake?.city || 'Unknown'}
Next Index Score: ${audit.scores ? Object.values(audit.scores).filter(s => s?.score > 0).reduce((sum, s) => sum + s.score, 0) / Math.max(Object.values(audit.scores).filter(s => s?.score > 0).length, 1) : 'N/A'}

Write in the style of a senior hospitality consultant. Be direct, commercially sharp, and hospitality-native. Return ONLY the rewritten text — no explanation, no quotes, no labels.`;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' },
        body: JSON.stringify({ model: 'claude-sonnet-4-6', max_tokens: 1000, messages: [{ role: 'user', content: prompt }] }),
      });

      const data = await response.json();
      const newText = data.content?.[0]?.text || '';

      if (newText) {
        const fieldMap = {
          diagnostic: 'execDiagnosticOverride',
          onePage: 'businessOnePageOverride',
          success: 'whatSuccessOverride',
        };
        updateField(fieldMap[refineTarget], newText.trim());
      }
    } catch (err) {
      console.error('Refine failed:', err);
    }

    setRefining(false);
    setRefinePrompt('');
  };

  return (
    <div>
      <h2 className="font-display text-xl text-white mb-1">Executive Content</h2>
      <p className="text-xs text-neutral-500 mb-6">Edit the AI-generated executive content. Use the refinement tool to have AI rewrite sections.</p>

      {/* Executive Diagnostic */}
      <div className="card p-5 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-brand-gold">Executive Diagnostic</h3>
          {ai.executiveDiagnostic && audit.execDiagnosticOverride && (
            <button onClick={() => updateField('execDiagnosticOverride', null)} className="text-[9px] text-neutral-600 hover:text-neutral-400">Reset to AI</button>
          )}
        </div>
        <textarea
          value={execDiagnostic}
          onChange={(e) => updateField('execDiagnosticOverride', e.target.value)}
          placeholder="The executive diagnostic summarizes the business health in 2-3 sharp sentences..."
          rows={4}
          className="input-field text-sm"
        />
        {!execDiagnostic && <p className="text-[10px] text-neutral-600 mt-1">Run AI Audit to generate, or write your own above.</p>}
      </div>

      {/* Business in One Page */}
      <div className="card p-5 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-brand-gold">The Business in One Page</h3>
          {ai.businessInOnePage && audit.businessOnePageOverride && (
            <button onClick={() => updateField('businessOnePageOverride', null)} className="text-[9px] text-neutral-600 hover:text-neutral-400">Reset to AI</button>
          )}
        </div>
        <textarea
          value={businessOnePage}
          onChange={(e) => updateField('businessOnePageOverride', e.target.value)}
          placeholder="A concise leadership snapshot — what is this business, who does it serve, what's most limiting it..."
          rows={5}
          className="input-field text-sm"
        />
      </div>

      {/* What Success Looks Like */}
      <div className="card p-5 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-brand-gold">What Success Looks Like</h3>
          {ai.whatSuccessLooksLike && audit.whatSuccessOverride && (
            <button onClick={() => updateField('whatSuccessOverride', null)} className="text-[9px] text-neutral-600 hover:text-neutral-400">Reset to AI</button>
          )}
        </div>
        <textarea
          value={whatSuccess}
          onChange={(e) => updateField('whatSuccessOverride', e.target.value)}
          placeholder="Describe what this business looks like 12 months from now if they execute on the roadmap..."
          rows={4}
          className="input-field text-sm"
        />
      </div>

      {/* AI Refinement Tool */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-white mb-3">AI Refinement</h3>
        <p className="text-xs text-neutral-500 mb-3">Tell the AI how to rewrite a section. Example: "Make it shorter" or "Focus more on the revenue opportunity" or "Tone down the criticism."</p>

        <div className="flex gap-2 mb-3">
          <select value={refineTarget} onChange={(e) => setRefineTarget(e.target.value)} className="input-field text-sm w-44">
            <option value="diagnostic">Executive Diagnostic</option>
            <option value="onePage">Business in One Page</option>
            <option value="success">What Success Looks Like</option>
          </select>
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={refinePrompt}
            onChange={(e) => setRefinePrompt(e.target.value)}
            placeholder="e.g., Make it more concise, focus on the revenue gap..."
            className="input-field text-sm flex-1"
            onKeyDown={(e) => e.key === 'Enter' && refineWithAI()}
          />
          <button onClick={refineWithAI} disabled={!refinePrompt.trim() || refining} className="btn-primary text-xs py-2 disabled:opacity-50">
            {refining ? 'Refining...' : 'Refine'}
          </button>
        </div>
      </div>
    </div>
  );
}
